import { useState } from 'react'
import { Pencil, Trash2, Send, Loader2, Target, Key, Copy } from 'lucide-react'
import toastService from '@/services/toast/service'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { TableSkeleton } from '@/components/skeletons'
import { useIsViewer, VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer'
import { trackingAdminService, PLATFORM_FUNNELS, PLATFORM_EVENTS, type AdminPixel } from '@/services/trackingAdmin'
import PixelEditModal from './PixelEditModal'

interface PixelsCardProps {
  pixels: AdminPixel[]
  loading: boolean
  onChanged: (next: AdminPixel[]) => void
  /** Owned by TrackingPage, whose header carries the Add button. */
  creating: boolean
  onCreatingChange: (creating: boolean) => void
}

const COLUMN_COUNT = 6

const funnelLabel = (v: string) => PLATFORM_FUNNELS.find((f) => f.value === v)?.label ?? v
const eventLabel = (v: string) => PLATFORM_EVENTS.find((e) => e.value === v)?.label ?? v

export default function PixelsCard({ pixels, loading, onChanged, creating, onCreatingChange }: PixelsCardProps) {
  const isViewer = useIsViewer()
  const [editing, setEditing] = useState<AdminPixel | null>(null)
  const [removing, setRemoving] = useState<AdminPixel | null>(null)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [testingId, setTestingId] = useState<number | null>(null)
  const [revealingId, setRevealingId] = useState<number | null>(null)
  const [revealed, setRevealed] = useState<{ pixel: AdminPixel; token: string } | null>(null)

  const handleTest = async (pixel: AdminPixel) => {
    setTestingId(pixel.id)
    try {
      const r = await trackingAdminService.testPixel(pixel.id)
      if (r.ok) {
        toastService.success(`Test event sent to ${pixel.label} (code ${r.testEventCode})`)
      } else {
        const msg = (r.body as { error?: { message?: string } } | null)?.error?.message
        toastService.error(msg ? `Couldn't verify the pixel: Meta says ${msg}` : `Couldn't verify the pixel: Meta returned HTTP ${r.httpStatus}`)
      }
      onChanged(await trackingAdminService.listPixels())
    } catch (e: any) {
      toastService.error(e?.response?.data?.message || "Couldn't send the test event")
    } finally {
      setTestingId(null)
    }
  }

  const handleReveal = async (pixel: AdminPixel) => {
    setRevealingId(pixel.id)
    try {
      setRevealed({ pixel, token: await trackingAdminService.revealToken(pixel.id) })
    } catch (e: any) {
      // The api client already toasts a server message; only cover the silent failures.
      if (!e?.response?.data?.message) toastService.error("Couldn't load the token")
    } finally {
      setRevealingId(null)
    }
  }

  const handleCopyToken = async () => {
    if (!revealed) return
    try {
      await navigator.clipboard.writeText(revealed.token)
      toastService.success('Token copied')
    } catch {
      toastService.error("Couldn't copy token")
    }
  }

  const handleRemove = async () => {
    if (!removing) return
    setRemoveBusy(true)
    try {
      await trackingAdminService.deletePixel(removing.id)
      onChanged(pixels.filter((p) => p.id !== removing.id))
      toastService.success('Pixel removed')
      setRemoving(null)
    } catch (e: any) {
      toastService.error(e?.response?.data?.message || "Couldn't remove the pixel")
    } finally {
      setRemoveBusy(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card">

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="min-w-[220px]">Pixel</TableHead>
              <TableHead className="text-center min-w-[180px]">Funnels</TableHead>
              <TableHead className="text-center min-w-[160px]">Events</TableHead>
              <TableHead className="text-center w-[120px]">Token</TableHead>
              <TableHead className="text-center w-[110px]">Status</TableHead>
              <TableHead className="text-center w-[160px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className={loading ? 'pointer-events-none' : ''}>
            {loading ? (
              <TableSkeleton columns={COLUMN_COUNT} rows={2} />
            ) : pixels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                  <div className="flex flex-col items-center justify-center">
                    <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                      <Target className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">No pixels yet</h3>
                    <p className="text-sm text-muted-foreground">
                      Add one to start sending Lead, StartTrial and Subscribe server-side.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              pixels.map((p) => {
                const testing = testingId === p.id
                const revealing = revealingId === p.id
                return (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{p.label}</span>
                        <span className="text-xs text-muted-foreground font-mono">{p.pixelId}</span>
                        {p.testEventCode && (
                          <span className="text-xs text-muted-foreground">Test code: {p.testEventCode}</span>
                        )}
                      </div>
                    </TableCell>

                    <TableCell className="text-center">
                      {p.funnels.length > 0 ? (
                        <div className="flex flex-wrap gap-1 justify-center">
                          {p.funnels.map((f) => (
                            <Badge key={f} variant="secondary" className="font-medium text-xs shadow-none cursor-default">
                              {funnelLabel(f)}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm italic">None</span>
                      )}
                    </TableCell>

                    <TableCell className="text-center">
                      {p.events.length > 0 ? (
                        <div className="flex flex-wrap gap-1 justify-center">
                          {p.events.map((e) => (
                            <Badge key={e} variant="secondary" className="font-medium text-xs shadow-none cursor-default">
                              {eventLabel(e)}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm italic">None</span>
                      )}
                    </TableCell>

                    <TableCell className="text-center font-mono text-xs text-muted-foreground">
                      {p.hasAccessToken ? `****${p.accessTokenLast4 ?? ''}` : <span className="text-destructive">Missing</span>}
                    </TableCell>

                    <TableCell className="text-center">
                      {p.enabled ? <Badge tone="success">Enabled</Badge> : <Badge tone="neutral">Disabled</Badge>}
                    </TableCell>

                    <TableCell>
                      <div className="flex justify-center">
                        <div className="inline-flex items-center rounded-md border">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-none rounded-l-md border-r"
                                  disabled={isViewer || testing || !p.testEventCode}
                                  onClick={() => handleTest(p)}
                                >
                                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {isViewer
                                ? VIEWER_NO_PERMISSION
                                : p.testEventCode
                                  ? 'Send test Lead'
                                  : 'Set a test event code first'}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-none border-r"
                                  disabled={isViewer || revealing}
                                  onClick={() => handleReveal(p)}
                                >
                                  {revealing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{isViewer ? VIEWER_NO_PERMISSION : 'Show access token'}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-none border-r"
                                  disabled={isViewer}
                                  onClick={() => setEditing(p)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{isViewer ? VIEWER_NO_PERMISSION : 'Edit pixel'}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-none rounded-r-md hover:bg-destructive/10 hover:text-destructive"
                                  disabled={isViewer}
                                  onClick={() => setRemoving(p)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{isViewer ? VIEWER_NO_PERMISSION : 'Remove pixel'}</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>


      <PixelEditModal
        open={creating || editing !== null}
        pixel={editing}
        onOpenChange={(open) => {
          if (!open) {
            onCreatingChange(false)
            setEditing(null)
          }
        }}
        onSaved={(saved) => {
          onChanged(editing ? pixels.map((p) => (p.id === saved.id ? saved : p)) : [...pixels, saved])
          onCreatingChange(false)
          setEditing(null)
        }}
      />

      <AlertDialog open={!!revealed} onOpenChange={(open) => !open && setRevealed(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Access token</AlertDialogTitle>
            <AlertDialogDescription>
              CAPI access token for <strong>{revealed?.pixel.label}</strong> ({revealed?.pixel.pixelId})
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogBody>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={revealed?.token ?? ''}
                className="font-mono text-xs"
                onFocus={(e) => e.target.select()}
              />
              <Button variant="outline" size="icon" className="shrink-0" onClick={handleCopyToken}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removing} onOpenChange={(open) => !open && !removeBusy && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              Server-side events stop for this pixel immediately. Past sends stay in Logs. The browser pixel in GTM is not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleRemove() }} disabled={removeBusy}>
              {removeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
